import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

const statusConfig = {
  active: { label: "Active", color: "var(--color-success)", bg: "var(--color-success)" },
  inactive: { label: "Inactive", color: "var(--color-warning)", bg: "var(--color-warning)" },
  suspended: { label: "Suspended", color: "var(--color-error)", bg: "var(--color-error)" },
};

export default function VendorTable() {
  const [vendorList, setVendorList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState("full_name");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberCounts, setMemberCounts] = useState({});
  const [revenueTotals, setRevenueTotals] = useState({});

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/super-admin/vendors');
      setVendorList(data || []);
      setMemberCounts({});
      setRevenueTotals({});
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const toggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await api.patch(`/super-admin/vendors/${id}/status`, { is_active: newStatus });
      setVendorList(prev => prev?.map(v => v?.id === id ? { ...v, is_active: newStatus } : v));
    } catch (err) {
      console.error('Error toggling vendor status:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelectedIds(prev => prev?.length === filteredVendors?.length ? [] : filteredVendors?.map(v => v?.id));
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const filteredVendors = vendorList
    ?.filter(v =>
      v?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      v?.gym_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      v?.email?.toLowerCase()?.includes(search?.toLowerCase())
    )
    ?.sort((a, b) => {
      const av = a?.[sortField]; const bv = b?.[sortField];
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const SortIcon = ({ field }) => (
    <Icon name={sortField === field ? (sortDir === "asc" ? "ChevronUp" : "ChevronDown") : "ChevronsUpDown"} size={12} color="currentColor" />
  );

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-8 flex items-center justify-center">
        <div className="flex items-center gap-2 text-[var(--color-muted-foreground)]">
          <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
          <span className="text-sm font-[var(--font-caption)]">Loading vendors...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[var(--color-border)]">
        <div>
          <h2 className="text-base font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Vendor Management</h2>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{filteredVendors?.length} vendors registered</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {selectedIds?.length > 0 && (
            <span className="text-xs text-[var(--color-primary)] font-medium font-[var(--font-caption)] px-2 py-1 bg-[var(--color-primary)]/10 rounded-md">{selectedIds?.length} selected</span>
          )}
          <div className="relative flex-1 sm:flex-none">
            <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-full sm:w-48"
            />
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors">
            <Icon name="Plus" size={13} color="white" />
            Add Vendor
          </button>
        </div>
      </div>
      {error && (
        <div className="mx-4 mt-3 p-2 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
          <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
              <th className="w-10 px-4 py-3">
                <input type="checkbox" checked={selectedIds?.length === filteredVendors?.length && filteredVendors?.length > 0} onChange={toggleAll} className="rounded" />
              </th>
              {[["full_name","Vendor"],["gym_name","Gym"],["email","Email"]]?.map(([field, label]) => (
                <th key={field} className="px-4 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide cursor-pointer hover:text-[var(--color-foreground)] whitespace-nowrap" onClick={() => handleSort(field)}>
                  <span className="flex items-center gap-1">{label} <SortIcon field={field} /></span>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">Members</th>
              <th className="px-4 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">Revenue</th>
              <th className="px-4 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {filteredVendors?.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  No vendors found
                </td>
              </tr>
            ) : (
              filteredVendors?.map(vendor => {
                const isActive = vendor?.is_active;
                const statusKey = isActive ? 'active' : 'inactive';
                const cfg = statusConfig?.[statusKey];
                const memberCount = memberCounts?.[vendor?.id] || 0;
                const revenue = revenueTotals?.[vendor?.id] || 0;
                return (
                  <tr key={vendor?.id} className={`hover:bg-[var(--color-muted)]/40 transition-colors ${selectedIds?.includes(vendor?.id) ? "bg-[var(--color-primary)]/5" : ""}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds?.includes(vendor?.id)} onChange={() => toggleSelect(vendor?.id)} className="rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-[var(--color-foreground)] font-[var(--font-body)]">{vendor?.full_name}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{vendor?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--color-foreground)] font-[var(--font-body)]">{vendor?.gym_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{vendor?.email}</td>
                    <td className="px-4 py-3 font-[var(--font-data)] text-[var(--color-foreground)]">{memberCount}</td>
                    <td className="px-4 py-3 font-[var(--font-data)] font-medium text-[var(--color-foreground)]">${revenue?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cfg?.bg }}></span>
                        <span className="text-xs font-medium font-[var(--font-caption)]" style={{ color: cfg?.color }}>{cfg?.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleStatus(vendor?.id, vendor?.is_active)}
                          className={`px-2.5 py-1 rounded text-xs font-medium font-[var(--font-caption)] transition-colors ${
                            isActive
                              ? "bg-[var(--color-error)]/10 text-[var(--color-error)] hover:bg-[var(--color-error)]/20"
                              : "bg-[var(--color-success)]/10 text-[var(--color-success)] hover:bg-[var(--color-success)]/20"
                          }`}
                        >
                          {isActive ? "Disable" : "Enable"}
                        </button>
                        <button className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors">
                          <Icon name="MoreHorizontal" size={14} color="var(--color-muted-foreground)" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
